import { api } from '@packages/backend/convex/_generated/api';
import { formatMobile, getUserErrorMessage } from '@packages/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { format, formatDistanceToNow } from 'date-fns';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { ErrorAlert } from '@/components/ErrorAlert';
import { SuperAdminOnly } from '@/components/SuperAdminOnly';
import { Badge } from '@/components/ui/badge';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { cn } from '@/utils/cn';

export const Route = createFileRoute('/r/$restaurantId/customers/')({
  component: Component,
});

function Component() {
  const { restaurantId } = Route.useParams();

  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: restaurantCustomers,
    isError,
    error,
    isPending,
  } = useQuery(api.restaurantCustomers.listByRestaurantId, { restaurantId });
  const restaurantQuery = useQuery(api.restaurants.getById, { restaurantId });

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading customers." />;
  }

  if (restaurantQuery.isError) return null;

  // Filter customers based on search query
  const filteredCustomers =
    !isPending && restaurantCustomers
      ? searchQuery.trim() === ''
        ? [...restaurantCustomers] // Create a new array to avoid reference issues
        : restaurantCustomers.filter((customer) => {
            if (!customer) return false;

            const query = searchQuery.toLowerCase().trim();
            const name = customer.name?.toLowerCase() || '';
            const email = customer.email?.toLowerCase() || '';
            const phoneFormats = normalizePhoneNumber(customer.phoneNumber);

            return (
              // Removes all spaces from the input phone number
              name.includes(query) ||
              email.includes(query) ||
              phoneFormats.some((format) => format.includes(query.replace(/\s+/g, '')))
            );
          })
      : [];

  // Sort customers so that inactive customers (those with empty name and email) appear last
  // Use a stable sort to maintain consistent order and prevent duplicates
  const sortedCustomers = filteredCustomers.slice().sort((a, b) => {
    const aIsInactive = !a.name && !a.email;
    const bIsInactive = !b.name && !b.email;

    if (aIsInactive && !bIsInactive) return 1; // a is inactive, b is active, so a comes after b
    if (!aIsInactive && bIsInactive) return -1; // a is active, b is inactive, so a comes before b

    // Add a secondary sort criteria to ensure consistent ordering
    if (a.username && b.username) {
      return a.username.localeCompare(b.username);
    }

    return 0;
  });

  return (
    <div className="p-6 min-h-[calc(100vh-(--spacing(16)))] flex-1 flex-col gap-4 bg-muted/60 dark:bg-muted/20">
      <Card>
        <CardHeader>
          <CardTitle>
            Customers {!isPending && restaurantCustomers ? `(${restaurantCustomers.length})` : ''}
          </CardTitle>
          <CardAction>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 w-full"
              />
            </div>
          </CardAction>
        </CardHeader>
        <CardContent>
          {isPending || restaurantQuery.isPending || restaurantQuery.data === null ? (
            <LoadingIcon />
          ) : (
            <CustomersTable
              filteredCustomers={sortedCustomers}
              loyaltyCardEnabled={restaurantQuery.data.loyaltyCardEnabled ?? false}
              loyaltyCardTarget={restaurantQuery.data.loyaltyCardTarget}
              restaurantId={restaurantId}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const CustomersTable = ({
  filteredCustomers,
  loyaltyCardEnabled,
  loyaltyCardTarget,
  restaurantId,
}: {
  filteredCustomers: any[];
  loyaltyCardEnabled: boolean;
  loyaltyCardTarget: number | null;
  restaurantId: string;
}) => {
  const convex = useConvex();
  const navigate = useNavigate({ from: Route.fullPath });

  const handleDisableUser = async (customerId: string) => {
    console.log('TODO: Implement disable customer...');
    // try {
    //   await convex.mutation(api.restaurantCustomers.toggleEnabled, {
    //     restaurantCustomerId: customerId,
    //     isEnabled: false,
    //   });
    //   toast.success('User disabled successfully');
    // } catch (error) {
    //   const description = handleErrorOnClient(error);
    //   toast.error('Unable to disable user!', { description });
    // }
  };

  const handleEnableUser = async (customerId: string) => {
    // try {
    //   await convex.mutation(api.restaurantCustomers.toggleEnabled, {
    //     restaurantCustomerId: customerId,
    //     isEnabled: true,
    //   });
    //   toast.success('User enabled successfully');
    // } catch (error) {
    //   const description = handleErrorOnClient(error);
    //   toast.error('Unable to enable user!', { description });
    // }
  };

  const handleCopyUsername = (username: string) => {
    navigator.clipboard.writeText(username);
    toast.success('Username copied to clipboard');
  };

  const handleSendPushNotification = async (customerId: string) => {
    try {
      await convex.mutation(api.pushNotifications.addToQueue, {
        sendToRestaurantCustomerId: customerId,
        expoPushNotification: {
          title: 'Test Notification',
          body: 'This is a test notification',
        },
      });
      toast.success('Push notification queued for sending');
    } catch (error) {
      toast.error("Couldn't send notification", {
        description: getUserErrorMessage(error),
      });
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Phone</TableHead>
          {loyaltyCardEnabled && loyaltyCardTarget !== null && (
            <TableHead className="hidden md:table-cell">Loyalty Card</TableHead>
          )}
          <TableHead className="hidden md:table-cell">Last ordered</TableHead>
          <TableHead className="hidden md:table-cell">Saved Credit Cards</TableHead>
          <SuperAdminOnly>
            <TableHead className="hidden md:table-cell">Enabled</TableHead>
          </SuperAdminOnly>
          <TableHead className="w-[64px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {/* TODO: Add ability to view order by customer, via ActionDropdown */}
        {/* TODO: Add ability to view customer in Stripe, via ActionDropdown */}
        {filteredCustomers.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={loyaltyCardEnabled && loyaltyCardTarget !== null ? 8 : 7}
              className="text-center py-6"
            >
              No customers found matching your search.
            </TableCell>
          </TableRow>
        ) : (
          filteredCustomers.map((c) => {
            return (
              <TableRow
                className={cn(
                  !c.isEnabled && 'text-muted-foreground/70 bg-muted/50',
                  !c.name && !c.email && 'text-muted-foreground/70 bg-muted/50',
                )}
                key={c._id}
              >
                <TableCell className="py-0">
                  <div className="font-medium">{c.name ?? '(No name)'}</div>
                </TableCell>
                <TableCell className="hidden md:table-cell py-0">{c.email}</TableCell>
                <TableCell className="hidden md:table-cell py-0">
                  {formatMobile(c.phoneNumber)}
                </TableCell>
                {loyaltyCardEnabled && loyaltyCardTarget !== null && (
                  <TableCell className="hidden md:table-cell py-0">
                    {/* Loyalty card stamps */}
                    {c.loyaltyCardBalance !== null && !!c.email && !!c.name && (
                      <div className="flex">
                        <div className="flex gap-1 h-[26px]">
                          {[...Array(c.loyaltyCardBalance)].map((_, index) => (
                            <img
                              // biome-ignore lint/suspicious/noArrayIndexKey: no other index
                              key={index}
                              src={'/coffee-cup-marked@3x.png'}
                              className={'w-[20px]'}
                              alt={'Loyalty stamp'}
                            />
                          ))}

                          {[...Array(Math.max(0, loyaltyCardTarget - c.loyaltyCardBalance))].map(
                            (_, index) => (
                              <img
                                // biome-ignore lint/suspicious/noArrayIndexKey: no other index
                                key={index}
                                src={'/coffee-cup-open@3x.png'}
                                className={'w-[20px]'}
                                alt={'Loyalty stamp placeholder'}
                              />
                            ),
                          )}
                        </div>
                        <img
                          src={'/coffee-cup-free@3x.png'}
                          className={'w-[28px]'}
                          alt={'Loyalty stamp'}
                        />
                      </div>
                    )}
                  </TableCell>
                )}

                <TableCell className="hidden md:table-cell py-0">
                  {c.lastOrderSubmittedAt ? (
                    <Tooltip>
                      <TooltipTrigger>
                        {formatDistanceToNow(c.lastOrderSubmittedAt, {
                          addSuffix: true,
                        })}
                      </TooltipTrigger>
                      <TooltipContent>
                        {format(c.lastOrderSubmittedAt, 'h:mm a, dd-MMM')}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell className="hidden md:table-cell py-0">{c.creditCards.length}</TableCell>
                <SuperAdminOnly>
                  <TableCell className="hidden md:table-cell">
                    <Badge
                      className={cn(
                        'text-xs text-green-600 dark:text-green-400',
                        !c.isEnabled && 'text-muted-foreground dark:text-muted-foreground',
                      )}
                      variant="secondary"
                    >
                      {c.isEnabled ? 'ENABLED' : 'DISABLED'}
                    </Badge>
                  </TableCell>
                </SuperAdminOnly>
                <TableCell className="w-[64px] py-0">
                  <ActionDropdown>
                    <DropdownMenuItem
                      onSelect={() =>
                        navigate({
                          to: '/r/$restaurantId/customers/$customerId',
                          params: {
                            restaurantId,
                            customerId: c._id,
                          },
                        })
                      }
                    >
                      View details
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => handleCopyUsername(c.username)}>
                      Copy username
                    </DropdownMenuItem>
                    {c.isEnabled ? (
                      <>
                        <DropdownMenuItem onSelect={() => handleSendPushNotification(c._id)}>
                          Send test push notification
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className={
                            'hover:text-destructive! dark:hover:text-red-300! text-destructive dark:hover:bg-destructive dark:text-red-400'
                          }
                          onSelect={() => handleDisableUser(c._id)}
                        >
                          Disable user
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem onSelect={() => handleEnableUser(c._id)}>
                        Enable user
                      </DropdownMenuItem>
                    )}
                  </ActionDropdown>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
};

// Helper function to normalize phone numbers for searching
const normalizePhoneNumber = (phone: string | null | undefined): string[] => {
  if (!phone) return [''];

  // Remove all non-digit characters except for the + sign at the beginning
  const cleaned = phone.replace(/[^\d+]/g, '');
  const formats = [cleaned.toLowerCase()];

  // If it's an Australian number in international format (+61), add the local format (0...)
  if (cleaned.startsWith('+61')) {
    formats.push(`0${cleaned.substring(3).toLowerCase()}`);
  }

  // If it's an Australian number in local format (0...), add the international format (+61...)
  if (cleaned.startsWith('0') && cleaned.length >= 10) {
    formats.push(`+61${cleaned.substring(1).toLowerCase()}`);
  }

  return formats;
};
